import { cn } from "web-design-system";
import { forwardRef } from "react";

const MFD_ROLE_LABEL = "AMFI Registered Mutual Fund Distributor";
const DISCLAIMER_LINE_1 =
  "Mutual Fund investments are subject to market risks.";
const DISCLAIMER_LINE_2 = "Read all scheme related documents carefully.";

export type WebStoryBrandingDetails = {
  distributorName: string;
  tagline?: string | null;
  arn: string;
  phoneDisplay: string;
  flagImageUrl?: string | null;
  brandLogoUrl?: string | null;
};

export function formatArnForEndCard(arn: string) {
  const code = arn.replace(/^ARN[-:\s]*/i, "").trim();
  return code ? `ARN : ${code}` : arn;
}

type WebStoryEndCardProps = {
  details: WebStoryBrandingDetails;
  width: number;
  height: number;
  className?: string;
};

export const WebStoryEndCard = forwardRef<HTMLDivElement, WebStoryEndCardProps>(
  function WebStoryEndCard({ details, width, height, className }, ref) {
    const {
      distributorName,
      tagline,
      arn,
      phoneDisplay,
      flagImageUrl,
      brandLogoUrl,
    } = details;

    return (
      <div
        ref={ref}
        className={cn(
          "flex flex-col items-center justify-between bg-background px-10 py-16 text-center",
          className
        )}
        style={{ width, height }}
      >
        <div className="flex w-full flex-1 flex-col items-center justify-center gap-8">
          {brandLogoUrl ? (
            <div className="size-20 overflow-clip rounded-2xl shrink-0">
              <img
                src={brandLogoUrl}
                alt={distributorName}
                className="h-20 w-20"
              />
            </div>
          ) : null}

          <div className="flex flex-col gap-4">
            <div className="max-w-full text-2xl font-semibold">
              {distributorName}
            </div>

            {tagline?.trim() ? (
              <div className="max-w-full text-xl">{tagline.trim()}</div>
            ) : null}

            {phoneDisplay.trim() ? (
              <div className="flex items-center justify-center gap-2 text-xl">
                {flagImageUrl ? (
                  <img
                    src={flagImageUrl}
                    alt=""
                    aria-hidden
                    crossOrigin="anonymous"
                    className="h-4 w-6 shrink-0 rounded-xs object-cover"
                  />
                ) : null}
                <span>{phoneDisplay.trim()}</span>
              </div>
            ) : null}
          </div>
        </div>

        <div className="flex w-full flex-col items-center gap-4 pb-16">
          <div className="text-xl font-semibold">
            {formatArnForEndCard(arn)}
          </div>
          <div className="max-w-full text-balance text-md text-muted-foreground">
            {MFD_ROLE_LABEL}
          </div>
        </div>

        <div className="flex w-full flex-col items-center gap-1 text-md text-muted-foreground">
          <span>{DISCLAIMER_LINE_1}</span>
          <span>{DISCLAIMER_LINE_2}</span>
        </div>
      </div>
    );
  }
);

WebStoryEndCard.displayName = "WebStoryEndCard";
